import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { invoicesApi } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus, HiOutlineDocumentText, HiPencil,
  HiEye, HiX, HiOutlineCurrencyRupee, HiOutlineSearch,
  HiOutlinePrinter, HiOutlineClock, HiOutlineTrash, HiOutlineChatAlt2
} from 'react-icons/hi';
import InvoiceForm from '../../components/Forms/InvoiceForm';
import PaymentForm from '../../components/Forms/PaymentForm';
import ExportInvoiceModal from '../../components/Forms/ExportInvoiceModal';
import DeleteReasonModal from '../../components/DeleteReasonModal';
import SendWhatsAppModal from '../../components/WhatsApp/SendWhatsAppModal';
import TruncatedName from '../../components/TruncatedName';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { usePermissions } from '../../hooks/usePermissions';
import {
  formatCalendarDateDMY,
  formatDateTimeShortIST,
} from '../../utils/timezoneDisplay';
import { INLINE_FILTER_BAR, INLINE_FILTER_FIELD } from '../../utils/inlineListFilters';

const formatDate = formatCalendarDateDMY;
const formatDateTime = formatDateTimeShortIST;

const isRowDeleted = (row) => Number(row?.is_deleted) === 1;

function parseExportMetadata(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return raw;
}

function normalizeExportMetadata(inv) {
  if (!inv) return null;
  let meta = parseExportMetadata(inv.export_metadata) || {};
  if (!Array.isArray(meta.items) || meta.items.length === 0) {
    if (Array.isArray(inv.items) && inv.items.length > 0) {
      meta = {
        ...meta,
        period_from: meta.period_from || inv.items[0]?.period_from || null,
        period_to: meta.period_to || inv.items[0]?.period_to || null,
        items: inv.items.map((row) => ({
          plan_name: row.description || '',
          per_session_charge: row.unit_price,
          no_of_sessions: row.quantity,
          total: row.amount,
          therapy_plan_id: row.therapy_plan_id || null,
        })),
      };
    }
  }
  if (!meta.payment_date && inv.invoice_date) {
    meta.payment_date = inv.invoice_date;
  }
  if (!meta.receipt_no && inv.invoice_number) {
    meta.receipt_no = inv.invoice_number;
  }
  return Object.keys(meta).length > 0 ? meta : null;
}

const Invoices = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patient_id');
  const focusId = searchParams.get('focusId');
  const openDetail = searchParams.get('openDetail') === '1';
  const deepSearch = searchParams.get('search');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [showTemplateInvoiceModal, setShowTemplateInvoiceModal] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingTemplateInvoice, setEditingTemplateInvoice] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showDownloadLogs, setShowDownloadLogs] = useState(false);
  const [downloadLogs, setDownloadLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '', status: '', payment_type: '', date_from: '', date_to: '', page: 1
  });
  const [pagination, setPagination] = useState({});
  const [highlightInvoiceId, setHighlightInvoiceId] = useState(null);
  const [invoiceFormPatient, setInvoiceFormPatient] = useState(null);
  const [invoiceFormAssessment, setInvoiceFormAssessment] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsappInvoice, setWhatsappInvoice] = useState(null);

  const debouncedSearch = useDebouncedValue(filters.search, 300);

  useEffect(() => {
    if (deepSearch) {
      setFilters((prev) => ({ ...prev, search: deepSearch, page: 1 }));
    }
  }, [deepSearch]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, debouncedSearch, filters.status, filters.payment_type, filters.date_from, filters.date_to, filters.page, showDeleted]);

  useEffect(() => {
    if (!openDetail || !focusId || loading) return;
    handleView({ id: focusId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDetail, focusId, loading, initialLoaded]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        ...(patientId ? { patient_id: patientId } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.payment_type ? { payment_type: filters.payment_type } : {}),
        ...(filters.date_from ? { date_from: filters.date_from } : {}),
        ...(filters.date_to ? { date_to: filters.date_to } : {}),
        ...(showDeleted ? { include_deleted: 1 } : {}),
        page: filters.page, limit: 20
      };
      const [invoicesRes] = await Promise.all([
        invoicesApi.getAll(params)
      ]);
      setInvoices(invoicesRes.data.data.invoices || []);
      setPagination(invoicesRes.data.data.pagination || {});
    } catch (error) {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  };

  const handleFilterChange = (name, value) => { setFilters({ ...filters, [name]: value, page: 1 }); };
  const clearFilters = () => { setFilters({ search: '', status: '', payment_type: '', date_from: '', date_to: '', page: 1 }); };
  const hasActiveFilters = filters.search || filters.status || filters.payment_type || filters.date_from || filters.date_to;

  const handleCreate = (patient = null) => {
    setSelectedInvoice(null);
    setInvoiceFormPatient(patient || null);
    setInvoiceFormAssessment(null);
    setShowForm(true);
  };

  const handleEdit = async (invoice) => {
    try {
      const response = await invoicesApi.getById(invoice.id);
      const inv = response.data.data;
      if (inv?.template_code) {
        setEditingTemplateInvoice(inv);
        setShowTemplateInvoiceModal(true);
        return;
      }
      setSelectedInvoice(inv);
      setShowForm(true);
    } catch (error) { toast.error('Failed to load invoice details'); }
  };

  const handleView = async (invoice) => {
    try {
      const response = await invoicesApi.getById(invoice.id);
      setDetailInvoice(response.data.data);
      setShowDetail(true);
    } catch (error) { toast.error('Failed to load invoice details'); }
  };

  const handleWhatsAppInvoice = (invoice) => {
    setWhatsappInvoice(invoice);
    setShowWhatsAppModal(true);
  };

  const previewInvoiceWhatsApp = async () => {
    const res = await invoicesApi.previewWhatsAppSend(whatsappInvoice.id);
    return res.data.data;
  };

  const sendInvoiceWhatsApp = async (data) => {
    await invoicesApi.sendWhatsApp(whatsappInvoice.id, data);
    fetchData();
  };

  const handleFormSuccess = () => {
    fetchData();
    if (showDetail && detailInvoice) handleView({ id: detailInvoice.id });
  };

  const handleStatusUpdate = async (invoiceId, newStatus) => {
    try {
      if (newStatus === 'paid') {
        toast.error('Invoice status cannot be set to "paid" directly. Add payment records.');
        return;
      }
      await invoicesApi.update(invoiceId, { status: newStatus });
      toast.success('Invoice status updated');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to update status'); }
  };

  const handleSendInvoice = async (invoice) => {
    try {
      await invoicesApi.send(invoice.id, { channel: 'email' });
      toast.success('Invoice marked as sent');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to send invoice'); }
  };

  const canDeleteInvoice = (inv) =>
    canDelete('invoices') &&
    parseFloat(inv.paid_amount || 0) === 0 && inv.status !== 'paid';

  const handleDelete = (invoice) => {
    if (!canDeleteInvoice(invoice)) {
      toast.error('Cannot delete an invoice that has payments recorded.');
      return;
    }
    setInvoiceToDelete(invoice);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteInvoice = async (deleteReason) => {
    if (!invoiceToDelete) return;
    setDeletingInvoice(true);
    try {
      await invoicesApi.delete(invoiceToDelete.id, { delete_reason: deleteReason });
      toast.success('Invoice deleted');
      if (showDetail && detailInvoice && String(detailInvoice.id) === String(invoiceToDelete.id)) {
        setShowDetail(false);
        setDetailInvoice(null);
      }
      setShowDeleteConfirm(false);
      setInvoiceToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setDeletingInvoice(false);
    }
  };

  const handleDirectDownload = async (invoice) => {
    try {
      const ok = window.confirm('Choose template:\n\nOK = Image template (Header + Footer)\nCancel = Default template');
      const render_style = ok ? 'letterhead' : 'default';
      // If invoice was generated from a DOCX template (ONGC/PRL), re-export via template endpoint.
      if (invoice?.template_code) {
        const response = await invoicesApi.getDocx(invoice.id, 'pdf', render_style);
        const serverMime = response.headers?.['content-type'] || 'application/pdf';
        const isPdf = /pdf/i.test(serverMime);
        const blob = new Blob([response.data], { type: serverMime });
        const ext = isPdf ? 'pdf' : 'docx';
        let filename = `Invoice_${invoice.invoice_number}.${ext}`;
        const cd = response.headers?.['content-disposition'];
        if (cd) {
          const m = /filename="?([^"]+)"?/i.exec(cd);
          if (m && m[1]) filename = m[1];
        }
        const fallbackReason = response.headers?.['x-pdf-fallback-reason'];
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        await invoicesApi.logDownload({
          invoice_id: invoice.id,
          download_type: isPdf ? 'pdf' : 'docx',
          notes_override: null
        });
        if (render_style === 'letterhead' && !isPdf) {
          toast(
            'Image template works only on PDF. This download is DOCX because PDF conversion failed on the server.',
            { icon: 'ℹ️', duration: 7000 }
          );
        } else if (fallbackReason === 'libreoffice-not-installed') {
          toast('Downloaded as DOCX — install LibreOffice on the server to enable PDF export.', { icon: 'ℹ️', duration: 6000 });
        } else if (fallbackReason) {
          toast('Downloaded as DOCX — PDF conversion failed.', { icon: 'ℹ️', duration: 5000 });
        } else {
          toast.success(isPdf ? 'Invoice downloaded successfully' : 'DOCX downloaded successfully');
        }
        return;
      }

      // Simple/default invoices use the PDF endpoint.
      const response =
        render_style === 'letterhead'
          ? await invoicesApi.getPdf(invoice.id, null, { render_style })
          : await invoicesApi.getPdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoice.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);

      await invoicesApi.logDownload({
        invoice_id: invoice.id,
        download_type: 'pdf',
        notes_override: null
      });
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const fetchDownloadLogs = async (invoiceId) => {
    setLogsLoading(true);
    try {
      const params = invoiceId ? { invoice_id: invoiceId } : {};
      const response = await invoicesApi.getDownloadLogs(params);
      setDownloadLogs(response.data.data.logs || []);
    } catch (error) { toast.error('Failed to fetch download logs'); }
    finally { setLogsLoading(false); }
  };

  const openDownloadLogs = (invoiceId) => {
    fetchDownloadLogs(invoiceId);
    setShowDownloadLogs(true);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-500 mt-1">Generate, print and manage invoices</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => openDownloadLogs()}
            className="btn btn-secondary"
            title="Download History"
          >
            <HiOutlineClock className="w-5 h-5 mr-2" />
            Download Logs
          </button>
          {canCreate('invoices') && (
            <button onClick={() => setShowTemplateInvoiceModal(true)} className="btn btn-primary">
              <HiOutlinePlus className="w-5 h-5 mr-2" />
              Create Invoice
            </button>
          )}
        </div>
      </div>

      <div className={INLINE_FILTER_BAR}>
        <div className="relative min-w-[9rem] flex-1 max-w-[14rem]">
          <HiOutlineSearch className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            placeholder="Search…"
            className={`${INLINE_FILTER_FIELD} w-full pl-8`}
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-auto min-w-[6.5rem] max-w-[8.5rem]`}
          aria-label="Status"
        >
          <option value="">All status</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={filters.payment_type}
          onChange={(e) => handleFilterChange('payment_type', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-auto min-w-[6.5rem] max-w-[9rem]`}
          aria-label="Payment type"
        >
          <option value="">All types</option>
          <option value="therapy">Therapy</option>
          <option value="assessment">Assessment</option>
          <option value="consultation">Consultation</option>
          <option value="other">Other</option>
        </select>
        <input
          type="date"
          value={filters.date_from}
          onChange={(e) => handleFilterChange('date_from', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-[8.75rem]`}
          aria-label="From date"
        />
        <span className="text-xs text-gray-400">–</span>
        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => handleFilterChange('date_to', e.target.value)}
          className={`${INLINE_FILTER_FIELD} w-[8.75rem]`}
          min={filters.date_from || undefined}
          aria-label="To date"
        />
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="whitespace-nowrap px-1 text-xs font-medium text-gray-500 hover:text-gray-800"
          >
            Clear
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setShowDeleted((prev) => !prev);
            setFilters((prev) => ({ ...prev, page: 1 }));
          }}
          className={`whitespace-nowrap rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
            showDeleted
              ? 'border-red-300 bg-red-50 text-red-700'
              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
          }`}
          aria-pressed={showDeleted}
        >
          Show deleted
        </button>
      </div>

            {/* Invoices list */}
      <div className="card">
        {loading && !initialLoaded ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <HiOutlineDocumentText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>{showDeleted ? 'No deleted invoices found' : 'No invoices found'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-header">Invoice #</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Patient</th>
                  <th className="table-header">Type</th>
                  <th className="table-header">Template</th>
                  <th className="table-header">Amount</th>
                  <th className="table-header">Balance</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoices.map((invoice) => {
                  const deleted = isRowDeleted(invoice);
                  return (
                  <tr
                    key={invoice.id}
                    className={`${
                      deleted
                        ? 'bg-red-50 text-red-800 border-l-4 border-red-400'
                        : `hover:bg-gray-50 ${
                            String(invoice.id) === String(focusId) || String(invoice.id) === String(highlightInvoiceId)
                              ? 'bg-primary-50'
                              : ''
                          }`
                    }`}
                  >
                    <td className="table-cell font-medium">
                      <span className="inline-flex items-center gap-2">
                        {invoice.invoice_number}
                        {deleted ? (
                          <span className="badge bg-red-200 text-red-800 text-xs">Deleted</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div>{formatDate(invoice.invoice_date)}</div>
                      {deleted && invoice.deleted_at ? (
                        <p className="text-xs text-red-600 mt-0.5">Deleted on {invoice.deleted_at}</p>
                      ) : null}
                      {deleted && invoice.delete_reason ? (
                        <p className="text-xs text-red-700 mt-0.5" title={invoice.delete_reason}>
                          Reason: {invoice.delete_reason}
                        </p>
                      ) : null}
                    </td>
                    <td className="table-cell">
                      <TruncatedName name={invoice.patient_name} maxLength={20}>
                        <p className="font-medium" />
                      </TruncatedName>
                    </td>
                    <td className="table-cell capitalize">{invoice.payment_type}</td>
                    <td className="table-cell">
                      {invoice.template_code ? (
                        <span className="badge bg-blue-100 text-blue-700 font-mono">
                          {invoice.template_code}
                        </span>
                      ) : (
                        <span className="badge bg-gray-100 text-gray-700">Default</span>
                      )}
                    </td>
                    <td className="table-cell">₹{parseFloat(invoice.total_amount).toLocaleString()}</td>
                    <td className="table-cell text-amber-600">₹{parseFloat(invoice.balance_amount).toLocaleString()}</td>
                    <td className="table-cell">
                      {deleted ? (
                        <span className="text-xs px-2 py-1 rounded-lg font-medium bg-red-100 text-red-700 capitalize">
                          {invoice.status}
                        </span>
                      ) : !canUpdate('invoices') ? (
                        <span className="text-xs px-2 py-1 rounded-lg font-medium bg-gray-100 text-gray-700 capitalize">
                          {invoice.status}
                        </span>
                      ) : (
                        <select
                          value={invoice.status}
                          onChange={(e) => handleStatusUpdate(invoice.id, e.target.value)}
                          disabled={invoice.status === 'paid'}
                          className={`text-xs px-2 py-1 rounded-lg border-0 font-medium ${
                            invoice.status === 'paid' ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
                          } ${
                            invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                            invoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                            invoice.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                            invoice.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                            invoice.status === 'cancelled' ? 'bg-gray-200 text-gray-700' :
                            'bg-gray-100 text-gray-700'
                          }`}
                        >
                          <option value="draft">Draft</option>
                          <option value="sent">Sent</option>
                          <option value="partial">Partial</option>
                          <option value="paid" disabled>Paid</option>
                          <option value="overdue">Overdue</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleView(invoice)}
                          className="p-1.5 hover:bg-gray-100 rounded-lg" title="View Details">
                          <HiEye className="w-4 h-4 text-primary-600" />
                        </button>
                        {!deleted ? (
                          <>
                            {canUpdate('invoices') && (
                              <button onClick={() => handleEdit(invoice)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg" title="Edit">
                                <HiPencil className="w-4 h-4 text-green-600" />
                              </button>
                            )}
                            <button onClick={() => handleDirectDownload(invoice)}
                              className="p-1.5 hover:bg-primary-50 rounded-lg" title="Print / Download">
                              <HiOutlinePrinter className="w-4 h-4 text-primary-600" />
                            </button>
                            <button
                              onClick={() => handleWhatsAppInvoice(invoice)}
                              className="p-1.5 hover:bg-emerald-50 rounded-lg"
                              title="Send via WhatsApp"
                            >
                              <HiOutlineChatAlt2 className="w-4 h-4 text-[#128C7E]" />
                            </button>
                            {invoice.status === 'draft' && canUpdate('invoices') && (
                              <button onClick={() => handleSendInvoice(invoice)}
                                className="text-blue-600 hover:text-blue-700 text-xs px-1.5 py-0.5">
                                Send
                              </button>
                            )}
                            {canDelete('invoices') && (
                            <button
                              type="button"
                              onClick={() => handleDelete(invoice)}
                              disabled={!canDeleteInvoice(invoice)}
                              title={canDeleteInvoice(invoice) ? 'Delete invoice' : 'Cannot delete: payments exist or invoice is paid'}
                              className={`p-1.5 rounded-lg ${
                                canDeleteInvoice(invoice)
                                  ? 'hover:bg-red-50 text-red-600'
                                  : 'opacity-40 cursor-not-allowed text-gray-400'
                              }`}
                            >
                              <HiOutlineTrash className="w-4 h-4" />
                            </button>
                            )}
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={pagination.page === 1} className="btn btn-secondary text-sm disabled:opacity-50">Previous</button>
              <button onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={pagination.page >= pagination.totalPages} className="btn btn-secondary text-sm disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Form Modal */}
      {showForm && (
        <InvoiceForm
          invoice={selectedInvoice}
          patient={invoiceFormPatient}
          assessment={invoiceFormAssessment}
          onClose={() => {
            setShowForm(false);
            setSelectedInvoice(null);
            setInvoiceFormPatient(null);
            setInvoiceFormAssessment(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Template Invoice Wizard Modal */}
      {showTemplateInvoiceModal && (
        <ExportInvoiceModal
          patient={editingTemplateInvoice?.patient_id
            ? { id: editingTemplateInvoice.patient_id, full_name: editingTemplateInvoice.patient_name, patient_code: editingTemplateInvoice.patient_code }
            : null}
          onClose={() => {
            setShowTemplateInvoiceModal(false);
            setEditingTemplateInvoice(null);
          }}
          onExported={() => fetchData()}
          step1Title="Create Template Invoice"
          allowedShapes={['ongc_weekly', 'ongc_session', 'ongc_general', 'prl_bill']}
          includeDefaultInvoiceOption={true}
          includeAssessmentInvoiceOption={true}
          onChooseDefaultInvoice={(patient) => {
            setShowTemplateInvoiceModal(false);
            setEditingTemplateInvoice(null);
            handleCreate(patient);
          }}
          onChooseAssessmentInvoice={(assessment) => {
            setShowTemplateInvoiceModal(false);
            setEditingTemplateInvoice(null);
            setInvoiceFormPatient(null);
            setInvoiceFormAssessment(assessment || null);
            setShowForm(true);
          }}
          onCreatedInvoiceId={(id) => setHighlightInvoiceId(String(id))}
          editingInvoiceId={editingTemplateInvoice?.id || null}
          initialTemplateCode={editingTemplateInvoice?.template_code || null}
          initialExportMetadata={normalizeExportMetadata(editingTemplateInvoice)}
        />
      )}

      {showDeleteConfirm && invoiceToDelete && (
        <DeleteReasonModal
          key={invoiceToDelete.id}
          title="Delete Invoice"
          message={`Delete invoice ${invoiceToDelete.invoice_number}?`}
          confirmLabel="Delete Invoice"
          deleting={deletingInvoice}
          onCancel={() => {
            if (deletingInvoice) return;
            setShowDeleteConfirm(false);
            setInvoiceToDelete(null);
          }}
          onConfirm={confirmDeleteInvoice}
        >
          <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600 space-y-1">
            <p>Patient: <span className="font-medium text-gray-900">{invoiceToDelete.patient_name || '—'}</span></p>
            <p>Amount: <span className="font-medium text-gray-900">₹{parseFloat(invoiceToDelete.total_amount || 0).toLocaleString()}</span></p>
          </div>
        </DeleteReasonModal>
      )}

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <PaymentForm invoice={detailInvoice}
          onClose={() => setShowPaymentForm(false)}
          onSuccess={handleFormSuccess} />
      )}

      {/* Download Logs Modal */}
      {showDownloadLogs && (
        <DownloadLogsModal
          logs={downloadLogs}
          loading={logsLoading}
          onClose={() => setShowDownloadLogs(false)}
        />
      )}

      {/* Invoice Detail Modal */}
      {showDetail && detailInvoice && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-4xl">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Invoice Details</h3>
              <button onClick={() => { setShowDetail(false); setDetailInvoice(null); }}
                className="text-gray-400 hover:text-gray-600"><HiX className="w-6 h-6" /></button>
            </div>
            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto">
              {isRowDeleted(detailInvoice) && detailInvoice.deleted_at ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-1">
                  <p>This invoice was deleted on {detailInvoice.deleted_at}.</p>
                  {detailInvoice.delete_reason ? (
                    <p><span className="font-medium">Reason:</span> {detailInvoice.delete_reason}</p>
                  ) : null}
                </div>
              ) : null}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Invoice Number</label>
                  <p className="text-gray-900 font-medium">{detailInvoice.invoice_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Invoice Date</label>
                  <p className="text-gray-900">{formatDate(detailInvoice.invoice_date)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Patient</label>
                  <p className="text-gray-900 font-medium">{detailInvoice.patient_name} ({detailInvoice.patient_code})</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Payment Type</label>
                  <p className="text-gray-900 capitalize">{detailInvoice.payment_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-1 block">Status</label>
                  {isRowDeleted(detailInvoice) ? (
                    <span className="inline-block text-xs px-3 py-1.5 rounded-lg font-medium bg-red-100 text-red-700 capitalize">
                      {detailInvoice.status}
                    </span>
                  ) : !canUpdate('invoices') ? (
                    <span className="inline-block text-xs px-3 py-1.5 rounded-lg font-medium bg-gray-100 text-gray-700 capitalize">
                      {detailInvoice.status}
                    </span>
                  ) : (
                    <>
                      <select
                        value={detailInvoice.status}
                        onChange={(e) => {
                          if (e.target.value === 'paid') { toast.error('Add payment records to mark as paid.'); return; }
                          handleStatusUpdate(detailInvoice.id, e.target.value);
                          setDetailInvoice({ ...detailInvoice, status: e.target.value });
                        }}
                        disabled={detailInvoice.status === 'paid'}
                        className={`text-xs px-3 py-1.5 rounded-lg border-0 font-medium ${
                          detailInvoice.status === 'paid' ? 'bg-green-100 text-green-700 cursor-not-allowed' :
                          detailInvoice.status === 'overdue' ? 'bg-red-100 text-red-700 cursor-pointer' :
                          detailInvoice.status === 'partial' ? 'bg-amber-100 text-amber-700 cursor-pointer' :
                          detailInvoice.status === 'sent' ? 'bg-blue-100 text-blue-700 cursor-pointer' :
                          'bg-gray-100 text-gray-700 cursor-pointer'
                        }`}>
                        <option value="draft">Draft</option>
                        <option value="sent">Sent</option>
                        <option value="partial">Partial</option>
                        <option value="paid" disabled>Paid</option>
                        <option value="overdue">Overdue</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      {detailInvoice.status !== 'paid' && parseFloat(detailInvoice.balance_amount) > 0 && canCreate('payments') && (
                        <button type="button" onClick={() => setShowPaymentForm(true)}
                          className="mt-2 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 flex items-center gap-1">
                          <HiOutlineCurrencyRupee className="w-4 h-4" /> Record Payment
                        </button>
                      )}
                    </>
                  )}
                </div>
                {detailInvoice.due_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Due Date</label>
                    <p className="text-gray-900">{formatDate(detailInvoice.due_date)}</p>
                  </div>
                )}
              </div>

              {detailInvoice.items && detailInvoice.items.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Items</label>
                  <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Rate</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {detailInvoice.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">{item.description}</td>
                          <td className="px-3 py-2 text-right">{item.quantity}</td>
                          <td className="px-3 py-2 text-right">₹{parseFloat(item.unit_price).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">₹{parseFloat(item.amount).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₹{parseFloat(detailInvoice.subtotal || 0).toFixed(2)}</span>
                </div>
                {detailInvoice.tax_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax:</span>
                    <span className="font-medium">₹{parseFloat(detailInvoice.tax_amount).toFixed(2)}</span>
                  </div>
                )}
                {detailInvoice.discount_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount:</span>
                    <span className="font-medium text-red-600">-₹{parseFloat(detailInvoice.discount_amount).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="font-semibold">Total Amount:</span>
                  <span className="font-bold text-lg text-primary-600">₹{parseFloat(detailInvoice.total_amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Paid:</span>
                  <span className="font-medium text-green-600">₹{parseFloat(detailInvoice.paid_amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Balance:</span>
                  <span className="font-medium text-amber-600">₹{parseFloat(detailInvoice.balance_amount).toFixed(2)}</span>
                </div>
              </div>

              {detailInvoice.payments && detailInvoice.payments.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Payment History</label>
                  <div className="space-y-2">
                    {detailInvoice.payments.map((payment, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">{payment.receipt_number}</span>
                          <span className="font-semibold text-green-600">₹{parseFloat(payment.amount).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{formatDate(payment.payment_date)}</span>
                          <span className="capitalize">{payment.payment_mode}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
              {!isRowDeleted(detailInvoice) && detailInvoice.status !== 'paid' && parseFloat(detailInvoice.balance_amount) > 0 && canCreate('payments') && (
                <button type="button" onClick={() => setShowPaymentForm(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
                  <HiOutlineCurrencyRupee className="w-4 h-4" /> Record Payment
                </button>
              )}
              <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 sm:ml-auto">
                {!isRowDeleted(detailInvoice) ? (
                  <>
                    {canDelete('invoices') && (
                    <button
                      type="button"
                      onClick={() => handleDelete(detailInvoice)}
                      disabled={!canDeleteInvoice(detailInvoice)}
                      title={canDeleteInvoice(detailInvoice) ? 'Delete invoice' : 'Cannot delete: payments exist or invoice is paid'}
                      className={`w-full sm:w-auto px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 border ${
                        canDeleteInvoice(detailInvoice)
                          ? 'border-red-300 text-red-700 hover:bg-red-50'
                          : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <HiOutlineTrash className="w-4 h-4" /> Delete
                    </button>
                    )}
                    <button onClick={() => { setShowDetail(false); handleDirectDownload(detailInvoice); }}
                      className="w-full sm:w-auto px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2">
                      <HiOutlinePrinter className="w-4 h-4" /> Print / Download
                    </button>
                    <button
                      onClick={() => handleWhatsAppInvoice(detailInvoice)}
                      className="w-full sm:w-auto px-4 py-2.5 bg-[#25D366] text-white rounded-lg hover:bg-[#1da851] flex items-center justify-center gap-2"
                    >
                      <HiOutlineChatAlt2 className="w-4 h-4" /> Send via WhatsApp
                    </button>
                  </>
                ) : null}
                <button onClick={() => { setShowDetail(false); setDetailInvoice(null); }}
                  className="w-full sm:w-auto px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SendWhatsAppModal
        open={showWhatsAppModal && !!whatsappInvoice}
        onClose={() => {
          setShowWhatsAppModal(false);
          setWhatsappInvoice(null);
        }}
        title="Send invoice via WhatsApp"
        documentLabel="Invoice"
        onPreview={previewInvoiceWhatsApp}
        onSend={sendInvoiceWhatsApp}
      />
    </div>
  );
};

/* ========== Download Logs Modal ========== */
const DownloadLogsModal = ({ logs, loading, onClose }) => (
  <div className="modal-shell flex items-center justify-center">
    <div className="modal-panel max-w-4xl">
      <div className="p-4 sm:p-5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <HiOutlineClock className="w-5 h-5 text-primary-500" />
          Invoice Download Logs
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <HiX className="w-6 h-6" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <HiOutlineDocumentText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p>No download logs yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date & Time</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Invoice #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Patient</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Downloaded By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map(log => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDateTime(log.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{log.invoice_number}</td>
                  <td className="px-4 py-3 text-gray-700">{log.patient_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{log.user_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      log.download_type === 'print' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {log.download_type?.toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      <div className="p-4 border-t border-gray-200 flex justify-end flex-shrink-0">
        <button onClick={onClose} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
          Close
        </button>
      </div>
    </div>
  </div>
);

export default Invoices;
